<script setup>
import { ref, useTemplateRef, inject, watch, onMounted, getCurrentInstance, nextTick } from 'vue';
import { FwbSpinner } from 'flowbite-vue';

import { useHubAuth } from '@base-shared/composables/use-hub-auth'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'
import { useLocalStorageStore } from '@base-shared/stores/local-storage'
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

defineOptions({
  mixins: [AuthMixin]
})

const $utils = inject('$utils');
const { proxy } = getCurrentInstance();

const iframeSrc = `https://${CONFIG.FRONTEND_HUB_DOMAIN}/auth.html`;
const isIframeHidden = ref(true);
const iframe = useTemplateRef('iframe');

const language = inject('$dataStore').getLanguage().code;
const $api = inject('$api')
const $router = inject('$routerInstance')

const hub = useHubAuth(iframeSrc);
const cognitoAuthStore = useCognitoAuthStore();

/**
 * Initialize the Hub Auth service.
 * This function sets up the Hub Auth service by initializing it with the iframe,
 * setting the language based on the locale cookie, and requesting storage access.
 */
async function initializeHub () {
  if (iframe.value === null) {
    throw new Error('Iframe reference is null. Cannot initialize Hub Auth.');
  }

  await hub.init(iframe.value);
  hub.setLanguage(language ?? 'en');
  isIframeHidden.value = false;
  await hub.requestForStorageAccess();
}

function setupCognitoAuthStore () {
  // Initialize cognito auth store with app's runtime config
  let amplifyAuthRedirectUrl = '';
  if (CONFIG) {
    const domain = `${window.location.protocol}//${window.location.host}/`;
    amplifyAuthRedirectUrl = domain;
  }

  const runtimeConfig = {
    globalStage: CONFIG?.GLOBAL_STAGE,
    awsCognitoUserPools: CONFIG?.AWS_COGNITO_USER_POOLS,
    amplifyAuthRedirectSignIn: amplifyAuthRedirectUrl,
    amplifyAuthRedirectSignOut: amplifyAuthRedirectUrl
  };
  cognitoAuthStore.initDefaultConfig(runtimeConfig);
  // Set the Hub Auth instance in the Cognito Auth Store
  cognitoAuthStore.setHubAuth(hub);
  // Then listen for AmplifyAuthEvents
  // TODO: Probably different handling for arya
  cognitoAuthStore.listenToAmplifyAuthEvents();
}

async function recoverSessionFromHub () {
  // Recover session with 5secs timeout
  if (hub.isHubEnabled()) {
    // attempt to recover session from HUB/Auth
    try {
      return await hub.retrieveAuthSession();
    } catch (error) {
      console.warn('Error getting user session from Hub Auth:', error);
    }
  }
}

async function handlePostAuthRouting () {
  // For ARYA Automatically setAuthReady to true and let the pages handle the routing themselves
  cognitoAuthStore.setAuthReady(true)
  isIframeHidden.value = true
}

async function handleExternalIdPRedirects (query) {
  if (query?.code && query?.state) {
    // Handle code and token exchange

    const localStorageStore = useLocalStorageStore();
    const isMigratingFromSocialAccount = localStorageStore.getIsMigratingFromSocialAccountFromStorage();

    if (isMigratingFromSocialAccount?.isMigrating) {
      const { provider } = isMigratingFromSocialAccount;
      await cognitoAuthStore.configureForCodeAndTokenExchange()
      localStorageStore.clearIsMigratingFromSocialAccountFromStorage();
      const urlQueryParams = $utils.encodeBase64Format({ showConfirm: 1, provider });
      await $router.push({ name: 'signIn', query: { sso_migrate_account: urlQueryParams } });
      cognitoAuthStore.setAuthReady(true);
      return;
    }

    // After SSO/SSI authorization, we need to configure amplify according to provider info and stage in localStorage
    try {
      const providerInfoAndCognitoStage = cognitoAuthStore.getProviderInfoAndCognitoStage()

      if (!providerInfoAndCognitoStage) {
        throw new Error('UnexpectedError', 'No provider info or Cognito stage found in local storage.');
      }

      if (providerInfoAndCognitoStage.SSOProviderName) {
        // Similar to cognitoAuthStore.configureForCodeAndTokenExchange(); but for some reason useSSO does not work correctly
        const providerResponse = await $api.get(`${CONFIG.API_BASE_URL}/sso/${providerInfoAndCognitoStage.SSOProviderName}`);
        const providerData = providerResponse.data;

        // Store the provider info in cognito auth store for later use
        cognitoAuthStore.setProviderInfoAndCognitoStage({
          CognitoStage: providerData.CognitoStage,
          CognitoClientAppID: providerData.CognitoClientAppID,
          SSOProviderName: providerData.SSOProviderName ?? providerData.CognitoIdPName
        });

        // Now configure Amplify with the provider
        const { success, error: configError } = cognitoAuthStore.configureAmplify();
        if (!success) {
          throw configError;
        }
      } else if (providerInfoAndCognitoStage.CognitoStage) {
        // If CognitoStage is set, configure with Stage
        await cognitoAuthStore.configureWithCognitoStage(providerInfoAndCognitoStage.CognitoStage);
      } else {
        // If no provider/stage info, configure with default stage
        await cognitoAuthStore.configureWithCognitoStage(cognitoAuthStore.runtimeConfig?.globalStage ?? '');
      }
    } catch (error) {
      console.error('Error handling external IdP redirects:', error);
    }
  }

  if (query?.error_description) {
    // Handle PreSignUpErrors in sign-in page
    const provider = cognitoAuthStore.getProviderInfoAndCognitoStage()?.SSOProviderName;

    const queryProvider = provider ? { provider } : {};
    $router.push({
      name: 'signIn',
      query: {
        ...query,
        ...queryProvider
      }
    });

    cognitoAuthStore.setAuthReady(true);
  }
}

watch(() => language, async () => {
  if (hub.isHubEnabled()) {
    hub.setLanguage(language ?? 'en');
  }
});

watch(() => cognitoAuthStore.isSigningIn, async (isSigningIn) => {
  $router.isReady().then(async () => {
    const route = $router.currentRoute.value;
    if (!route?.query.code || !route?.query.state) {
    // If no code and token exchange in progress, we do not want to
    // redirect to index page since it will be handled differently
      return;
    }

    // Once the sign-in process is done, set the auth ready state and remount pages to retrieve the user session
    if (!cognitoAuthStore.isAuthReady && !isSigningIn) {
      cognitoAuthStore.setAuthReady(true);
      // Wait until the components start mounting after setAuthReady(true) before we check for terms of service, otherwise the modal will not show up
      await nextTick()
      // TODO(auth-flow): This will be removed once we refactor tos check to middlewares
      await proxy.checkTermsOfServices({
        callback: () => {
          $utils.mainLoader(true);
          // Once TOS acceptance is done, we need to refresh the user session to get the latest tokens and user attributes
          EventBus.$emit('REFRESH_USER_SESSION');

          // REFRESH_USER_SESSION will trigger the USER_SESSION_UPDATED event once the session is refreshed, so we can handle the post-authentication actions here
          EventBus.$on('USER_SESSION_UPDATED', () => {
            $utils.mainLoader(false);
            proxy.sendSignedInEvent();
            $utils.animate('#signin', 'animated fadeOutLeft');
            $router.push('/loginok', () => {});
          });
        }
      });
    }
  });
})

onMounted(async () => {
  $router.isReady().then(async () => {
    const route = $router.currentRoute.value

    try {
      await initializeHub();
    } catch (error) {
      console.error('Error initializing Hub Auth:', error);
    }

    setupCognitoAuthStore();

    if ((route.query.code && route.query.state) || route.query.error_description) {
      // Prioritize handling of redirects after external IdP authentication
      await handleExternalIdPRedirects(route.query);
      return;
    }

    const ssoMatch = /(?:^|#)\/sso\/([^/?#]+)(?:[?#]|$)/.exec(route?.fullPath);
    if (ssoMatch) {
      // Note: When deployed, because of the hash prefix (/#/), we need to
      // check for the fullPath instead of the route name to determine if
      // it's an SSO redirect. The regex ensures only paths in the form
      // `/sso/<provider>` or `#/sso/<provider>` are matched, and captures
      // the provider name directly from the first capture group.
      const providerName = ssoMatch[1];
      await $router.push({ name: 'ssoStartPage', params: { providerName } });
      cognitoAuthStore.setAuthReady(true);
      return;
    }

    // No initial value so we can fallback to default localStorage if hub is not enabled
    const authSession = await recoverSessionFromHub();

    // Initialize the auth session
    await cognitoAuthStore.initializeAuthSession(authSession);

    // Now, we need to handle redirects based on authentication state
    await handlePostAuthRouting();
  });
});
</script>

<template>
  <div>
    <div
      v-show="!isIframeHidden"
      class="flex justify-center items-center h-screen"
    >
      <iframe
        id="authIframe"
        ref="iframe"
        data-uc-allowed="true"
        :src="iframeSrc"
        :class="{
          'hidden': isIframeHidden,
          'w-[866px]': !isIframeHidden,
          'h-[308px]': !isIframeHidden,
          'w-px': isIframeHidden,
          'h-px': isIframeHidden,
          'top-0': isIframeHidden,
          'left-0': isIframeHidden,
          'absolute': true,
          'z-50': true,
          'border-0': true
        }"
        sandbox="allow-scripts allow-same-origin allow-storage-access-by-user-activation"
      />
    </div>
    <div
      v-show="isIframeHidden"
      class="flex justify-center items-center h-screen"
      data-component-element-id="pre-auth-loading"
    >
      <FwbSpinner size="12" />
    </div>
  </div>
</template>
